import { Typography } from '@/components/atoms/Typography';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useUIColors } from '@/core/hooks';
import React from 'react';
import { Pressable, StyleSheet, type ViewStyle, View } from 'react-native';

export interface SelectionChipProps {
  label: string;
  /** Optional second line (e.g. Today / Tomorrow) */
  sublabel?: string;
  selected?: boolean;
  disabled?: boolean;
  onPress?: () => void;
  /** Wider chip for dates vs compact for times (desktop / mobile) */
  size?: 'date' | 'time' | 'timeMobile';
}

/**
 * Selectable pill/card used for appointment slot date and time options.
 */
export function SelectionChip({
  label,
  sublabel,
  selected = false,
  disabled = false,
  onPress,
  size = 'time',
}: Readonly<SelectionChipProps>) {
  const colors = useUIColors();
  const isDate = size === 'date';
  const isTimeMobile = size === 'timeMobile';

  const backgroundColor = selected
    ? colors.buttonDefault
    : colors.backgroundLevel1;
  const borderColor = selected
    ? colors.buttonDefault
    : UI_PRIMITIVES.borderSlate;
  let labelColor: 'white' | 'tertiary' | 'primary' = 'primary';
  if (selected) {
    labelColor = 'white';
  } else if (disabled) {
    labelColor = 'tertiary';
  }

  let sizeStyle: ViewStyle = styles.chipTime;
  if (isDate) {
    sizeStyle = styles.chipDate;
  } else if (isTimeMobile) {
    sizeStyle = styles.chipTimeMobile;
  }

  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityState={{ selected, disabled }}
      style={[
        styles.chip,
        sizeStyle,
        {
          backgroundColor,
          borderColor,
          opacity: disabled && !selected ? 0.55 : 1,
        },
      ]}
    >
      <View
        style={[
          styles.textCol,
          isDate && sublabel ? styles.textColDate : null,
        ]}
      >
        <Typography
          variant="caption"
          color={labelColor}
          style={styles.chipLabel}
        >
          {label}
        </Typography>
        {sublabel ? (
          <Typography
            variant="caption"
            color={selected ? 'white' : 'tertiary'}
            style={styles.chipLabel}
          >
            {sublabel}
          </Typography>
        ) : null}
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  chip: {
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  /** Figma date chip: ~79.8×68, radius 12, inner gap 4 */
  chipDate: {
    width: 80,
    height: 68,
    borderRadius: 12,
  },
  /** Desktop time chip: 97×52, radius 10 */
  chipTime: {
    width: 97,
    height: 52,
    borderRadius: 10,
    paddingTop: 16,
    paddingRight: 20,
    paddingBottom: 16,
    paddingLeft: 20,
  },
  /** Mobile time chip — fills equal grid columns */
  chipTimeMobile: {
    width: '100%',
    height: 48,
    borderRadius: 12,
    paddingTop: 14,
    paddingRight: 20,
    paddingBottom: 14,
    paddingLeft: 20,
  },
  textCol: {
    alignItems: 'center',
    gap: 2,
  },
  /** Figma date chip inner gap (date ↔ Today/Tomorrow) */
  textColDate: {
    gap: 4,
  },
  /** Font/Size/XS Medium, line-height 100% */
  chipLabel: {
    fontFamily: 'Matter-Medium',
    fontWeight: '500',
    fontSize: 12,
    lineHeight: 12,
    textAlign: 'center',
  },
});

export default SelectionChip;
